/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FC, useMemo } from 'react';
import { groupBy } from 'lodash-es';
import { MdRefresh } from 'react-icons/md';

import Countup from 'components/styled/Countup';
import Text from 'components/styled/Text';
import { Flex } from 'components/styled';
import IconButton from 'components/styled/IconButton';

import { pluralRules } from 'utils';

import { useHomepageStatistics } from 'api/publicApi';

import { useLocalizedMultistring } from 'utils/localization';

const InfoElem: FC<{
	id: string;
	title: number;
	subtitle: string;
}> = ({ id, title, subtitle }) => (
	<Flex
		flexDirection="column"
		alignItems="center"
		justifyContent="center"
		mb={0}
		mx={[2, 3]}
	>
		<Countup id={id} fontSize={30} fontWeight={800} m={0} textAlign="center">
			{title}
		</Countup>
		<Text
			fontSize={[13, 'md']}
			fontWeight="normal"
			m={0}
			textAlign="center"
			css={css`
				white-space: nowrap;
			`}
		>
			{subtitle}
		</Text>
	</Flex>
);

const CountupInfoLiberec: FC = () => {
	const localize = useLocalizedMultistring();
	const response = useHomepageStatistics();

	const {
		studentTotal: [studentTotal] = [],
		companyTotalVisible: [companyTotalVisible] = [],
		offerTotal: [offerTotal] = [],
	} = useMemo(() => groupBy(response.data ?? [], 'statType'), [response.data]);

	return (
		<Flex justifyContent="center" my={0} minHeight={83}>
			{response.isError ? (
				<IconButton
					color="white"
					onClick={() => response.refetch()}
					aria-label={localize({
						CZECH: 'Zkusit načíst znovu statistiky',
						ENGLISH: 'Try to reload statistics',
					})}
				>
					<MdRefresh size={22} />
					<Text>
						{localize({
							CZECH: 'Zkusit načíst znovu statistiky',
							ENGLISH: 'Try to reload statistics',
						})}
					</Text>
				</IconButton>
			) : (
				<>
					<InfoElem
						id="companies_countup"
						title={companyTotalVisible?.count ?? 0}
						subtitle={pluralRules(
							companyTotalVisible?.count ?? 0,
							{
								one: localize({
									CZECH: 'zaměstnavatel',
									ENGLISH: 'employer',
								}),
								few: localize({
									CZECH: 'zaměstnavatelé',
									ENGLISH: 'employers',
								}),
								many: localize({
									CZECH: 'zaměstnavatelů',
									ENGLISH: 'employers',
								}),
							},
							false,
						)}
					/>
					<InfoElem
						id="students_countup"
						title={studentTotal?.count ?? 0}
						subtitle={pluralRules(
							studentTotal?.count ?? 0,
							{
								one: localize({
									CZECH: 'student',
									ENGLISH: 'student',
								}),
								few: localize({
									CZECH: 'studenty',
									ENGLISH: 'students',
								}),
								many: localize({
									CZECH: 'studentů',
									ENGLISH: 'students',
								}),
							},
							false,
						)}
					/>

					<InfoElem
						id="offers_countup"
						title={offerTotal?.count ?? 0}
						subtitle={pluralRules(
							offerTotal?.count ?? 0,
							{
								one: localize({
									CZECH: 'pracovní pozice',
									ENGLISH: 'job offer',
								}),
								few: localize({
									CZECH: 'pracovní pozice',
									ENGLISH: 'job offers',
								}),
								many: localize({
									CZECH: 'pracovních pozic',
									ENGLISH: 'job offers',
								}),
							},
							false,
						)}
					/>
				</>
			)}
		</Flex>
	);
};

export default CountupInfoLiberec;
